/**
 * 素材库 API —— 对接 animiz.cn 可商用音乐素材
 *
 * 开发环境通过 Vite proxy (/materialapi → https://www.animiz.cn/materialapi) 转发，
 * 生产环境直连 https://www.animiz.cn/materialapi。
 * 路径定义在 AxiosMode.MATERIAL_API_HOST 中。
 */

import { AxiosMode, type ApiResponse } from '@/axiosModel';
import { API_TAXONOMY_CODES } from '@/constants/apiConstants';

const apis = new AxiosMode();
const API = AxiosMode.MATERIAL_API_HOST;

// ============================================================
// 类型定义
// ============================================================

/** 分类 / 标签通用结构 */
export interface MaterialCategory {
  code: string;
  name: string;
  vip: string;
  poster: string;
}

export interface MaterialItem {
  id: string;
  name: string;
  url: string;
  /** 时长（秒） */
  duration: number;
}

/** 分类查询可选参数 */
export interface FetchCategoriesParams {
  /** 软件 code，支持多选，用 "," 分割（如 focusky,animiz） */
  app?: string;
  /** 关键词搜索，支持多条件精准搜索，用空格隔开 */
  keyword?: string;
  /** 排序：2 为按时间倒序 */
  order?: string;
  /** VIP 类型（整型） */
  vip?: string;
  /** 是否商用 Y｜N */
  commercial?: string;
  /** 是否显示关键词 true｜false */
  isShowKeyword?: string;
}

interface TaxonomyData {
  taxonomy: MaterialCategory;
  categories: MaterialCategory[];
  tags: MaterialCategory[];
}

/** 素材列表查询可选参数 */
export interface FetchMaterialListParams {
  /** tag code，多个用英文逗号连接 */
  tag?: string;
  /** VIP 类型 */
  vip?: string;
  /** 是否商用 Y｜N */
  commercial?: string;
  /** 关键词 AND 搜索 */
  keyword?: string;
  /** 模糊搜索标题 */
  contain?: string;
  /** 多关键词 OR 搜索，多个用英文逗号区隔 */
  keywordOr?: string;
  /** 整型素材 ID */
  uid?: string;
  /** 文件类型，2:flash，多个用逗号隔开 */
  type?: string;
  /** id_2 搜索 */
  id_2?: string;
  /** md5 搜索 */
  md5?: string;
  /** 控制素材显示与否，edition <= 传入值 */
  edition?: string;
  /** 素材 ID 搜索，支持多选，英文逗号分隔，结果按 ids 顺序排列 */
  ids?: string;
  /** 排序：1 默认，2 按时间排序 */
  order?: string;
  /** 过滤应用素材，如 animiz */
  app?: string;
}

/** /query 接口返回的单条素材原始结构 */
interface QueryListItem {
  id: string;
  identity: string;
  md5: string;
  name: string;
  size: string;
  url: string;
  thumbnail: string;
  vip: string;
  commercial: string;
  composite: string[];
  /** 实际返回为对象，含 duration（秒）、isAIMaterial 等 */
  profile: { duration: number; [key: string]: unknown };
  tag: string;
}

interface QueryData {
  /** 总条数，做分页用 */
  total: number;
  list: QueryListItem[];
}

// ============================================================
// API 函数
// ============================================================

/**
 * 获取素材风格分类列表
 * @param code  分类体系 code，默认 BGM
 * @param params 可选查询参数
 */
export async function fetchMaterialCategories(
  code: string = API_TAXONOMY_CODES.BGM,
  params?: FetchCategoriesParams
): Promise<MaterialCategory[]> {
  const postData: Record<string, unknown> = { code, ...params };
  const { req } = apis.post({
    postData,
    remotePath: `${API}/taxonomy`
  });
  const res = (await req) as ApiResponse<TaxonomyData>;
  return res.data.categories ?? [];
}

/**
 * 获取指定分类下的素材列表
 * @param categoryCode 分类 code（由 fetchMaterialCategories 返回）
 * @param page         页码，从 1 开始
 * @param pageSize     每页条数
 * @param params       可选查询参数
 */
export async function fetchMaterialList(
  categoryCode: string,
  page = 1,
  pageSize = 20,
  params?: FetchMaterialListParams
): Promise<MaterialItem[]> {
  const postData: Record<string, unknown> = {
    category: categoryCode,
    page,
    pagesize: pageSize,
    ...params
  };
  const { req } = apis.post({
    postData,
    remotePath: `${API}/query`
  });
  const res = (await req) as ApiResponse<QueryData>;
  return (res.data.list ?? []).map((item) => ({
    id: item.id,
    name: item.name,
    url: item.url,
    duration: item.profile?.duration ?? 0
  }));
}
